import { FormikContextType } from 'formik';
import i18n from 'i18next';

import { ICalculationClassUI, OOControlModelInterface } from '../interfaces';

// German SSN (Rentenversicherungsnummer): area (2) + birth date DDMMYY (6) + birth name initial (1) + serial (2) + check digit (1)
const BIRTH_NAME_CONTROL = 'BIRTH_NAME';
const DATE_OF_BIRTH_CONTROL = 'DATE_OF_BIRTH';
const ERROR_PREFIX = 'DOCUMENTS2.DOCUMENT_UPLOAD2.social-security-number';
const BIRTH_DATE_MISMATCH_ERROR = `${ERROR_PREFIX}.birthDateMismatchError`;
const BIRTH_NAME_MISMATCH_ERROR = `${ERROR_PREFIX}.birthNameMismatchError`;
const BIRTH_DATE_AND_NAME_MISMATCH_ERROR = `${ERROR_PREFIX}.birthDateAndNameMismatchError`;

// "Ä" -> "A" etc., the SSN holds the base letter of the birth name initial
const getInitial = (name?: string | null) =>
  name
    ?.toString()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .charAt(0)
    .toUpperCase() || null;

// "2007-02-07" or "2007-02-07T00:00:00.000Z" -> "070207", read as-is so the timezone cannot shift the day
const toSsnDate = (dateOfBirth?: string | null) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOfBirth?.toString() ?? '');
  return match ? `${match[3]}${match[2]}${match[1].slice(2)}` : null;
};

export class LocalSSNValidation implements ICalculationClassUI {
  control: OOControlModelInterface;
  relevantControls: OOControlModelInterface[];
  allControls: OOControlModelInterface[];
  formik: FormikContextType<Record<string, any>>;

  constructor(
    control: OOControlModelInterface,
    formControls: OOControlModelInterface[] | undefined,
    formik: FormikContextType<Record<string, any>>,
  ) {
    this.control = control;
    this.allControls = formControls ?? [];
    this.formik = formik;
    this.relevantControls = this.allControls.filter((c) =>
      [BIRTH_NAME_CONTROL, DATE_OF_BIRTH_CONTROL].includes(c.name),
    );
  }

  init(value?: any) {
    if (value) {
      this.validate(value);
    }
  }

  onChange = (event: any) => {
    this.validate(event?.target?.value);
  };

  getError(value?: string): string | null {
    const ssn = (value ?? '').toString().replace(/\s/g, '').toUpperCase();
    const { values } = this.formik;

    const birthDate = toSsnDate(values[DATE_OF_BIRTH_CONTROL]);
    const ssnDay = +ssn.slice(2, 4);
    const ssnDate = `${(ssnDay > 50 ? ssnDay - 50 : ssnDay).toString().padStart(2, '0')}${ssn.slice(4, 8)}`;
    const birthDateMismatch = ssn.length >= 8 && !!birthDate && ssnDate !== birthDate;

    const initial = getInitial(values[BIRTH_NAME_CONTROL]);
    const birthNameMismatch = ssn.length >= 9 && !!initial && ssn.charAt(8) !== initial;

    if (birthDateMismatch && birthNameMismatch) {
      return BIRTH_DATE_AND_NAME_MISMATCH_ERROR;
    }
    if (birthDateMismatch) {
      return BIRTH_DATE_MISMATCH_ERROR;
    }
    if (birthNameMismatch) {
      return BIRTH_NAME_MISMATCH_ERROR;
    }
    return null;
  }

  validate(value?: string) {
    const error = this.getError(value);
    if (!error) {
      return;
    }
    const { name } = this.control;
    const ssn = (value ?? '').toString().replace(/\s/g, '').toUpperCase();
    const { values } = this.formik;
    const params = {
      area: ssn.slice(0, 2),
      ssnDate: ssn.slice(2, 8),
      ssnLetter: ssn.charAt(8),
      birthDate: toSsnDate(values[DATE_OF_BIRTH_CONTROL]) ?? '',
      birthName: values[BIRTH_NAME_CONTROL] ?? '',
      initial: getInitial(values[BIRTH_NAME_CONTROL]) ?? '',
    };
    // set after formik's own validation of this change, otherwise it overwrites the error
    setTimeout(() => {
      this.formik.setFieldTouched(name, true, false);
      this.formik.setFieldError(
        name,
        i18n.t(error, { ns: 'documents', ...params, interpolation: { escapeValue: false } }),
      );
    }, 0);
  }
}
